import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { apiFetch } from "../utils/api";
import SEOAnalyzer from "./SEO/SEOAnalyzer";
import SEOHistoryTable from "./SEO/SEOHistoryTable";
import SEOAdminDashboard from "./SEO/SEOAdminDashboard";
import ConfirmModal from "./ui/ConfirmModal";
import { 
  MdAutoFixHigh, 
  MdHistory, 
  MdAnalytics,
  MdInfoOutline
} from "react-icons/md";
import "./SEO.css";

// const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:9500/api/v1";

const SEO = () => {
  const { user } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState("analyzer");
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // State from Analyzer (to allow restoring from history)
  const [restoredReport, setRestoredReport] = useState(null);

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: '',
    message: '',
    confirmText: 'Delete',
    variant: 'danger',
    confirmPhrase: '',
    loading: false,
    onConfirm: null,
  });

  const isAdmin = user?.role?.toUpperCase() === "ADMIN" || user?.role?.toUpperCase() === "SUPERADMIN";

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await apiFetch("/seo/history");
      if (!res.ok) throw new Error("Failed to load history");
      const data = await res.json();
      setHistory(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      console.error("Failed to load history:", err);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const handleLoadFromHistory = async (item) => {
    try {
      const res = await apiFetch(`/seo/history/${item._id}`);
      if (!res.ok) throw new Error("Failed to load report");
      const data = await res.json();
      setRestoredReport(data);
      setActiveTab("analyzer");
    } catch (err) {
      console.error("Failed to load report details:", err);
    }
  };

  const handleDeleteHistory = (id, e) => {
    if (e) e.stopPropagation();
    setConfirmModal({
      show: true,
      title: 'Delete SEO Report',
      message: 'Are you sure you want to delete this report?',
      detail: 'This will permanently remove the audit report from the system. Cost metrics associated with it will be preserved for administrative purposes.',
      confirmText: 'Delete Report',
      variant: 'danger',
      confirmPhrase: 'confirm',
      loading: false,
      onConfirm: () => executeDeleteHistory(id),
    });
  };

  const executeDeleteHistory = async (id) => {
    setConfirmModal(prev => ({ ...prev, loading: true }));
    try {
      const res = await apiFetch(`/seo/history/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete");
      setHistory(prev => prev.filter(h => h._id !== id));
      setConfirmModal(prev => ({ ...prev, show: false, loading: false }));
    } catch (err) {
      console.error("Failed to delete history item:", err);
      setConfirmModal(prev => ({ ...prev, loading: false }));
    }
  };

  const closeConfirmModal = () => {
    setConfirmModal(prev => ({ ...prev, show: false, loading: false }));
  };

  return (
    <div className="seo-page-wrapper">
      <div className="seo-top-nav shadow-sm bg-white p-3 mb-4 sticky-top">
        <div className="container-fluid d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            <div className="icon-badge bg-primary text-white p-2 rounded">
              <MdAutoFixHigh size={24} />
            </div>
            <div>
              <h4 className="m-0 fw-bold">SEO Enterprise Analyzer</h4>
              <small className="text-muted">Production-grade AI Auditing</small>
            </div>
          </div>
          
          <div className="nav-tabs-custom d-flex gap-1 bg-light p-1 rounded">
            <button 
              className={`btn btn-sm ${activeTab === 'analyzer' ? 'btn-white shadow-sm' : 'btn-light'}`}
              onClick={() => setActiveTab('analyzer')}
            >
              <MdAutoFixHigh className="me-1" /> Analyzer
            </button>
            <button 
              className={`btn btn-sm ${activeTab === 'history' ? 'btn-white shadow-sm' : 'btn-light'}`}
              onClick={() => setActiveTab('history')}
            >
              <MdHistory className="me-1" /> History
            </button>
            {isAdmin && (
              <button 
                className={`btn btn-sm ${activeTab === 'admin' ? 'btn-white shadow-sm' : 'btn-light'}`}
                onClick={() => setActiveTab('admin')}
              >
                <MdAnalytics className="me-1" /> Admin Metrics
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="container-fluid px-4 pb-5">
        {activeTab === 'analyzer' && (
          <SEOAnalyzer 
            restoredData={restoredReport} 
            onAnalysisComplete={fetchHistory}
          />
        )}
        
        {activeTab === 'history' && (
          <div className="card border-0 shadow-sm">
            <SEOHistoryTable 
              history={history} 
              onLoad={handleLoadFromHistory}
              onDelete={handleDeleteHistory}
            />
          </div>
        )}
        
        {activeTab === 'admin' && isAdmin && (
          <SEOAdminDashboard />
        )}

        {/* Footer Info */}
        <div className="mt-5 text-center text-muted">
          <small className="d-flex align-items-center justify-content-center gap-1">
            <MdInfoOutline /> All analysis is session-bound. Costs are tracked for budget governance.
          </small>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        variant={confirmModal.variant}
        confirmPhrase={confirmModal.confirmPhrase}
        loading={confirmModal.loading}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirmModal}
      />
    </div>
  );
};

export default SEO;
